import MapboxDraw, { DrawCustomMode, DrawCustomModeThis, DrawFeature } from "@mapbox/mapbox-gl-draw";
import lineSplit from "@turf/line-split";
import { Feature, LineString } from "geojson";
import { defer } from "lodash";

import { lineIntersect } from "fond/turf";
import { isLineString } from "fond/types/geojson";

import { defaultOptions, HIGHLIGHT_PROPERTY_NAME, PASSING_MODE_NAME } from "./constants";

const {
  constants: { events },
} = MapboxDraw;

type ExtendedDrawCustomMode = DrawCustomMode & {
  fireUpdate(features: DrawFeature[]): void;
};

/**
 * A custom drawing mode that allows the user to select LineString(s) & then draw
 * an overlapping LineString that will split the selected lines where the overlap
 * occurs.
 */
const splitLineStringMode: ExtendedDrawCustomMode = {
  toDisplayFeatures: function toDisplayFeatures(_, geojson, display) {
    display(geojson);
  },

  onSetup: function onSetup(opt) {
    const { highlightColor = defaultOptions.highlightColor } = opt || {};
    const originalFeatures = this.getSelected()
      .filter((feat) => feat.type === "LineString")
      .map((feat) => feat.toGeoJSON());

    if (originalFeatures.length < 1) {
      throw new Error("Please select a feature/features (LineString) to split!");
    }

    const { api } = this._ctx;

    // The `onSetup` job should complete for this mode to work.
    // so we defer to bypass mode change until after `onSetup` is done executing.
    defer(() => {
      this.changeMode(PASSING_MODE_NAME, {
        onDraw: (splittingLineString: Feature<LineString>) => {
          const allUpdatedFeatures: DrawFeature[] = [];

          originalFeatures.forEach((feature) => {
            if (feature.type !== "Feature" || !isLineString(feature)) return;

            // Check for intersection first
            const intersections = lineIntersect(feature, splittingLineString);
            if (intersections.features.length === 0) return;

            // Split the line
            const splitResult = lineSplit(feature, splittingLineString);
            if (splitResult.features.length <= 1) return;

            const [firstSegment, ...restSegments] = splitResult.features;

            if (!feature.id) return;

            // First segment: update the original feature in-place (keeps ID and properties)
            const existingDrawFeature = this.getFeature(feature.id);
            if (!existingDrawFeature || existingDrawFeature.type !== "LineString") return;

            existingDrawFeature.setCoordinates(firstSegment.geometry.coordinates);
            // Remove highlight property
            api.setFeatureProperty(String(feature.id), HIGHLIGHT_PROPERTY_NAME, undefined);
            allUpdatedFeatures.push(existingDrawFeature);

            // Look up the layer config to determine how to assign properties to new segments
            const allLayers = this._ctx.options?.editOptions?.allLayers;
            const layerId = feature.properties?.layerId;
            const layerConfig = allLayers?.find((l: { ID: string }) => l.ID === layerId);
            const getFeatureProperties = layerConfig?.getFeatureProperties;

            const newFeatures: DrawFeature[] = [];

            // Remaining segments: create new features
            restSegments.forEach((segment) => {
              // Filter out degenerate segments
              if (segment.geometry.coordinates.length < 2) return;

              const properties = getFeatureProperties
                ? getFeatureProperties({ ...segment, properties: feature.properties } as Feature)
                : { ...feature.properties };

              const newFeature = this.newFeature({
                type: "Feature",
                properties,
                geometry: segment.geometry,
              });
              this.addFeature(newFeature);
              allUpdatedFeatures.push(newFeature);
              newFeatures.push(newFeature);
            });

            // Call onSplit if defined on the layer config
            if (layerConfig?.onSplit && newFeatures.length > 0) {
              layerConfig.onSplit.call(this, existingDrawFeature, newFeatures, intersections.features);
            }
          });

          // Remove the passing mode's temporary splitting line before firing events
          // that trigger history capture. The passing mode's onStop deletes this feature
          // AFTER onDraw returns, but fireUpdate triggers draw.update → updateHistory
          // which captures drawControl.getAll(). Without this cleanup the temporary line
          // (with degenerate geometry) is stored in history and crashes the snap library
          // when restored via undo.
          if (splittingLineString.id) {
            this.deleteFeature(String(splittingLineString.id), { silent: true });
          }

          // Fire a single update event for all affected features
          if (allUpdatedFeatures.length > 0) {
            this.fireUpdate(allUpdatedFeatures);
          }

          // Defer setting the id property to match the MapboxDraw-assigned id
          defer(() => {
            allUpdatedFeatures.forEach((f) => {
              f.setProperty("id", f.id);
            });
          });
        },
        onCancel: () => {
          // Remove highlighted features
          originalFeatures.forEach((feature) => {
            if (feature.type === "Feature" && feature.id) api.setFeatureProperty(String(feature.id), HIGHLIGHT_PROPERTY_NAME, undefined);
          });
        },
      });
    });

    // Set highlight feature property for features to split
    originalFeatures.forEach((feature) => {
      if (feature.type === "Feature" && feature.id) api.setFeatureProperty(String(feature.id), HIGHLIGHT_PROPERTY_NAME, highlightColor);
    });

    return {
      originalFeatures,
    };
  },

  fireUpdate: function fireUpdate(this: DrawCustomModeThis, features) {
    this.map.fire(events.UPDATE, {
      action: "split_feature",
      features: features.map((f) => f.toGeoJSON()),
    });
  },
};

export default splitLineStringMode;
